import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiClock,
  FiCalendar,
  FiPlus,
  FiTrash2,
  FiEdit,
  FiBell,
  FiRefreshCw,
  FiHome,
  FiLayers,
  FiShoppingBag,
  FiTool,
} from "react-icons/fi";
import {
  getMeetingReminders,
  createMeetingReminder,
  updateMeetingReminder,
  deleteMeetingReminder,
} from "../../../api/customer/customerFeatureApis";
import Modal from "../../common/modal/Modal";
import FormField from "../../common/formkit/FormField";
import { showToast } from "../../common/notification/Notification";
import { parseDate, formatDateTime, toDateTimeInputValue } from "../../../utils/date";

const REMINDER_OPTIONS = [
  { label: "5 minutes before", value: 5 },
  { label: "15 minutes before", value: 15 },
  { label: "30 minutes before", value: 30 },
  { label: "1 hour before", value: 60 },
];

const MeetingRemindersHub = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all"); // 'all' | 'property' | 'services' | 'local_bazaar' | 'interior'

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    dateTime: "",
    reminderMinutes: 15,
    referenceType: "property",
    referenceId: 0,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);

  const loadReminders = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);

    try {
      const res = await getMeetingReminders(token, {
        referenceType: activeFilter === "all" ? "property" : activeFilter,
        referenceId: 0,
        pageNumber: 1,
        pageSize: 100,
        orderBy: "DESC",
      });
      const list = res?.meetingReminders || res?.reminders || [];
      setReminders(list);
    } catch (err) {
      setError("Failed to fetch reminders. Please check your connection.");
    } finally {
      setLoading(false);
    }
  }, [token, activeFilter]);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  const handleOpenAdd = () => {
    setEditingReminder(null);
    // Default to tomorrow 10:00 AM
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);

    const pad = (n) => String(n).padStart(2, "0");
    const formatted = `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(
      tomorrow.getDate()
    )}T${pad(tomorrow.getHours())}:${pad(tomorrow.getMinutes())}`;

    setFormData({
      title: "",
      dateTime: formatted,
      reminderMinutes: 15,
      referenceType: "property",
      referenceId: 0,
    });
    setFormErrors({});
    setModalError(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (reminder, e) => {
    e?.stopPropagation();
    setEditingReminder(reminder);

    const dateVal = toDateTimeInputValue(reminder.meeting_date_time);

    setFormData({
      title: reminder.title || "",
      dateTime: dateVal,
      reminderMinutes: reminder.reminder_before_minutes || 15,
      referenceType: reminder.property_id
        ? "property"
        : reminder.all_services_id
        ? "all_services"
        : reminder.local_bazaar_id
        ? "local_bazaar"
        : reminder.interiror_design_id
        ? "interiror_design"
        : "property",
      referenceId:
        reminder.property_id ||
        reminder.all_services_id ||
        reminder.local_bazaar_id ||
        reminder.interiror_design_id ||
        0,
    });
    setFormErrors({});
    setModalError(null);
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = "Reminder title is required";
    }
    if (!formData.dateTime) {
      errors.dateTime = "Meeting date and time is required";
    } else {
      const selectedDate = parseDate(formData.dateTime);
      if (!selectedDate || selectedDate <= new Date()) {
        errors.dateTime = "Please select a future date and time";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setModalError(null);

    const pad = (n) => String(n).padStart(2, "0");
    const d = new Date(formData.dateTime);
    const apiFormattedDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}:00`;

    const payload = {
      title: formData.title.trim(),
      meeting_date_time: apiFormattedDate,
      reminder_before_minutes: Number(formData.reminderMinutes),
      property_id: formData.referenceType === "property" ? Number(formData.referenceId || 0) : 0,
      local_bazaar_id: formData.referenceType === "local_bazaar" ? Number(formData.referenceId || 0) : 0,
      interiror_design_id: formData.referenceType === "interiror_design" ? Number(formData.referenceId || 0) : 0,
      all_services_id: formData.referenceType === "all_services" ? Number(formData.referenceId || 0) : 0,
    };

    try {
      const res = editingReminder
        ? await updateMeetingReminder(token, { ...payload, id: editingReminder.id })
        : await createMeetingReminder(token, payload);

      if (res?.statusType === "SUCCESS" || res?.meetingReminder || res?.text) {
        showToast(editingReminder ? "Reminder updated successfully!" : "Meeting reminder scheduled!", {
          type: "success",
        });
        setIsModalOpen(false);
        loadReminders();
      } else {
        setModalError(res?.text || "Failed to schedule reminder.");
        showToast(res?.text || "Failed to schedule reminder.", { type: "error" });
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "An error occurred.";
      setModalError(message);
      showToast(message, { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReminder = async (reminder, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete reminder "${reminder.title}"?`)) return;

    try {
      await deleteMeetingReminder(token, reminder.id);
      showToast("Reminder deleted.", { type: "success" });
      setReminders((prev) => prev.filter((r) => r.id !== reminder.id));
    } catch (err) {
      alert("Failed to delete reminder.");
    }
  };

  const formatMeetingDate = (dateVal) => formatDateTime(dateVal, { fallback: "Upcoming" });

  return (
    <div className="d-flex flex-column gap-4 w-100 mx-auto" style={{ maxWidth: "1280px" }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div>
          <h1 className="fs-4 fw-bold text-dark mb-1">Meeting & Inspection Reminders</h1>
          <p className="text-muted small mb-0">
            Schedule property site visits, client discussions, and service appointments with automated alerts.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-primary px-3 d-inline-flex align-items-center gap-2 shadow-sm"
          style={{ minHeight: "40px" }}
          onClick={handleOpenAdd}
        >
          <FiPlus size={16} />
          <span>Schedule New Reminder</span>
        </button>
      </div>

      {/* Filter Tabs & Refresh */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 border-bottom pb-3">
        <div className="d-flex gap-2 flex-wrap">
          {[
            { id: "all", label: "All Reminders" },
            { id: "property", label: "Properties" },
            { id: "all_services", label: "Services" },
            { id: "local_bazaar", label: "Local Bazaar" },
            { id: "interiror_design", label: "Interior Design" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`btn rounded-pill px-3 ${
                activeFilter === tab.id ? "btn-primary" : "btn-light text-secondary border"
              }`}
              style={{ minHeight: "38px" }}
              aria-pressed={activeFilter === tab.id}
              onClick={() => setActiveFilter(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center gap-2 px-3"
          style={{ minHeight: "38px" }}
          onClick={loadReminders}
          disabled={loading}
        >
          <FiRefreshCw size={14} className={loading ? "spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Reminders List */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="text-muted small mt-2">Loading upcoming reminders...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={loadReminders}>
            Retry
          </button>
        </div>
      ) : reminders.length === 0 ? (
        <div className="text-center py-5 bg-white rounded-3 border border-slate-200 shadow-sm p-4">
          <div
            className="d-inline-flex align-items-center justify-content-center text-primary rounded-circle mb-3"
            style={{ width: "56px", height: "56px", background: "#eff6ff" }}
          >
            <FiClock size={28} />
          </div>
          <h3 className="fs-5 fw-bold text-dark mb-1">No Reminders Scheduled</h3>
          <p className="text-muted small mx-auto mb-3" style={{ maxWidth: "420px" }}>
            Keep track of client calls, inspections, and site visits by creating your first reminder.
          </p>
          <button type="button" className="btn btn-primary px-4" onClick={handleOpenAdd}>
            Schedule a Meeting
          </button>
        </div>
      ) : (
        <div className="row g-3">
          {reminders.map((rem) => {
            const isProperty = Boolean(rem.property_id);
            const isService = Boolean(rem.all_services_id);
            const isBazaar = Boolean(rem.local_bazaar_id);
            const isInterior = Boolean(rem.interiror_design_id);

            return (
              <div key={rem.id} className="col-12 col-md-6 col-lg-4">
                <div className="p-4 bg-white rounded-3 border border-slate-200 shadow-sm h-100 d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="badge bg-primary-subtle text-primary px-2 py-1 small fw-semibold d-inline-flex align-items-center gap-1">
                        {isProperty && <FiHome size={12} />}
                        {isService && <FiTool size={12} />}
                        {isBazaar && <FiShoppingBag size={12} />}
                        {isInterior && <FiLayers size={12} />}
                        <span>
                          {isProperty
                            ? "Property Visit"
                            : isService
                            ? "Service Booking"
                            : isBazaar
                            ? "Bazaar Inquiry"
                            : isInterior
                            ? "Interior Consultation"
                            : "General Meeting"}
                        </span>
                      </span>

                      <div className="d-flex align-items-center gap-1 text-muted small">
                        <FiBell size={13} />
                        <span>{rem.reminder_before_minutes || 15}m alert</span>
                      </div>
                    </div>

                    <h3 className="fs-6 fw-bold text-dark mb-2">{rem.title}</h3>

                    <div className="d-flex align-items-center gap-2 text-primary small fw-semibold mb-2">
                      <FiCalendar size={14} />
                      <span>{formatMeetingDate(rem.meeting_date_time)}</span>
                    </div>
                  </div>

                  <div className="pt-3 border-top border-slate-100 d-flex align-items-center justify-content-between">
                    <span className="text-muted small">Status: Active</span>

                    <div className="d-flex align-items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary py-1 px-2"
                        onClick={(e) => handleOpenEdit(rem, e)}
                        title="Edit reminder"
                      >
                        <FiEdit size={14} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger py-1 px-2"
                        onClick={(e) => handleDeleteReminder(rem, e)}
                        title="Delete reminder"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Reminder Modal */}
      <Modal
        open={isModalOpen}
        onClose={() => !submitting && setIsModalOpen(false)}
        title={editingReminder ? "Edit Reminder" : "Schedule Meeting Reminder"}
        icon={<FiClock size={20} />}
        accent="blue"
        footer={
          <>
            <button
              type="button"
              className="ahd-btn ahd-btn--outline"
              onClick={() => setIsModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" form="reminder-form" className="ahd-btn ahd-btn--primary" disabled={submitting}>
              {submitting && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
              <span>{submitting ? "Saving..." : editingReminder ? "Update Reminder" : "Save Reminder"}</span>
            </button>
          </>
        }
      >
        <form id="reminder-form" onSubmit={handleFormSubmit} noValidate>
          {modalError && (
            <div className="alert alert-danger py-2 small" role="alert">
              {modalError}
            </div>
          )}
          <div className="ahd-form-grid">
            <FormField
              id="reminder-title"
              label="Reminder Title / Purpose"
              required
              span
              placeholder="e.g. Property Inspection with Client"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              error={formErrors.title}
            />
            <FormField
              id="reminder-date"
              label="Date & Time"
              type="datetime-local"
              required
              value={formData.dateTime}
              onChange={(e) => setFormData((prev) => ({ ...prev, dateTime: e.target.value }))}
              error={formErrors.dateTime}
            />
            <FormField
              id="reminder-offset"
              as="select"
              label="Notify Me"
              value={formData.reminderMinutes}
              onChange={(e) => setFormData((prev) => ({ ...prev, reminderMinutes: Number(e.target.value) }))}
            >
              {REMINDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </FormField>
            <FormField
              id="reminder-type"
              as="select"
              label="Related Service"
              span
              value={formData.referenceType}
              onChange={(e) => setFormData((prev) => ({ ...prev, referenceType: e.target.value }))}
            >
              <option value="property">Property</option>
              <option value="all_services">Home Service</option>
              <option value="local_bazaar">Local Bazaar</option>
              <option value="interiror_design">Interior Design</option>
            </FormField>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default MeetingRemindersHub;
